import React, { Fragment } from "react";
import { useTranslation } from "react-i18next";
import { useStore } from "@/lib/store";
import { Lock, BarChart3, Users, Activity, Sparkles } from "lucide-react";
import { apiRequest } from "@/lib/queryClient";
import { useMutation } from "@tanstack/react-query";
import { useAnalytics } from "@/hooks/useAnalytics";
import { useAuth } from "@/hooks/useAuth";
import { getModuleIcon } from "@/utils/module-icons";
import { Link, useLocation } from "wouter";
import { useMonitoringAlertCount } from "./monitoring-alerts-panel";
import { standardAllowedModules } from "@shared/module-access";

// Mapa kolorów ambient per segment — brand red #ef3e29 = rgb(239,62,41)
const COLORS = {
  home: "239 62 41",
  "offer-builder": "239 62 41",
  "vsl-creator": "239 62 41",
  "ad-creator": "239 62 41",
  "marketing-analyst": "239 62 41",
  "campaign-creator": "239 62 41",
  "allegro-ads": "239 62 41",
  "reel-creator": "239 62 41",
  "idea-bank": "239 62 41",
  "youtube-mode": "239 62 41",
};

// Mapowanie nazw modułów
const MODULE_CONFIG: Record<string, { labelKey: string }> = {
  "offer-builder": { labelKey: "modules.offerBuilder" },
  "vsl-creator": { labelKey: "modules.vslCreator" },
  "ad-creator": { labelKey: "modules.adCreator" },
  "marketing-analyst": { labelKey: "modules.marketingAnalyst" },
  "campaign-creator": { labelKey: "modules.campaignCreator" },
  "allegro-ads": { labelKey: "modules.allegroAds" },
  "reel-creator": { labelKey: "modules.reelCreator" },
  "idea-bank": { labelKey: "modules.ideaBank" },
  "youtube-mode": { labelKey: "modules.youtubeMode" },
};

export default function ModuleTabs() {
  const { t } = useTranslation();
  const { modules, currentModule, setCurrentModule, setCurrentChatId } = useStore();
  const { trackModuleSwitch } = useAnalytics();
  const { user } = useAuth();
  const [location, setLocation] = useLocation();

  // Get user's allowed modules (null = all access; opt-in modules stripped — see shared/module-access.ts)
  const allowedModules = standardAllowedModules((user as any)?.allowedModules ?? null);
  const hiddenModules: string[] | null = (user as any)?.hiddenModules ?? null;
  const hasDashboardAccess = allowedModules === null || allowedModules.includes("dashboard");
  const isDashboardActive = location === "/dashboard";
  const userRole = (user as any)?.role;
  const isAgencyEmployee = userRole === "employee" && !!(user as any)?.agencyId;
  const isManagerOrOwner =
    userRole === "manager" || userRole === "agency_owner" || userRole === "super_admin";
  const hasManagerDashboardAccess = isAgencyEmployee || isManagerOrOwner;
  const isManagerDashboardActive = location === "/moiklienci";
  const isMonitoringActive = currentModule === "monitoring";
  const isHomeActive = currentModule === "home";
  const monitoringAlertCount = useMonitoringAlertCount();

  // Create new conversation mutation
  const createConversationMutation = useMutation({
    mutationFn: async (data: { moduleId: string }) => {
      return await apiRequest("POST", "/api/conversations", data);
    },
    onSuccess: (newConversation) => {
      setCurrentChatId(newConversation.id);
    },
  });

  const handleModuleChange = (moduleId: string, locked: boolean) => {
    if (!locked) {
      // If switching to different module, create new conversation
      if (moduleId !== currentModule) {
        trackModuleSwitch(currentModule, moduleId);
        setCurrentModule(moduleId);
        setLocation(`/chat/${moduleId}`);

        createConversationMutation.mutate({ moduleId });
      } else if (!location.startsWith(`/chat/${moduleId}`)) {
        // Same module but URL is off (coming from /dashboard, etc.)
        setLocation(`/chat/${moduleId}`);
      }
    }
  };

  if (!modules || modules.length === 0) {
    return (
      <div className="w-full bg-[#030407] border-b border-white/[0.08] py-4 px-4">
        <div className="text-white">{t("moduleTabs.loading")}</div>
      </div>
    );
  }

  // Tymczasowo ukryj tylko clone-yourself, email-creator
  const hiddenIds = new Set(["clone-yourself", "email-creator"]);
  const hiddenByUser = new Set(hiddenModules ?? []);
  const visibleModules = modules
    .filter((m) => !hiddenIds.has(m.id))
    .filter((m) => !hiddenByUser.has(m.id))
    .filter((m) => allowedModules === null || allowedModules.includes(m.id));

  return (
    <nav className="assistants-bar relative" aria-label="Asystenci">
      <div className="bar-wrap mx-auto max-w-screen-2xl">
        <ul className="bar-list">
          {/* Divider na początku */}
          <div className="divider" />

          {/* Dashboard tab */}
          {hasDashboardAccess && (
            <>
              <li
                className="segment"
                data-id="dashboard"
                data-active={isDashboardActive ? "true" : "false"}
                style={{
                  ["--amb" as any]: "239 62 41",
                  ["--overscan" as any]: "10px",
                }}
              >
                <button
                  type="button"
                  onClick={() => setLocation("/dashboard")}
                  className="focus-visible:outline-none"
                  aria-current={isDashboardActive ? "page" : undefined}
                />
                <div className="content">
                  <span className="h-5 w-5 text-white">
                    <BarChart3 className="w-5 h-5" />
                  </span>
                  <span className="text-sm md:text-[0.95rem] text-white">Dashboard</span>
                </div>
              </li>
              <div className="divider" />
            </>
          )}

          {/* Manager Dashboard tab - visible to agency employees/managers */}
          {hasManagerDashboardAccess && (
            <>
              <li
                className="segment"
                data-id="manager"
                data-active={isManagerDashboardActive ? "true" : "false"}
                style={{
                  ["--amb" as any]: "239 62 41",
                  ["--overscan" as any]: "10px",
                }}
              >
                <button
                  type="button"
                  onClick={() => setLocation("/moiklienci")}
                  className="focus-visible:outline-none"
                  aria-current={isManagerDashboardActive ? "page" : undefined}
                />
                <div className="content">
                  <span className="h-5 w-5 text-white">
                    <Users className="w-5 h-5" />
                  </span>
                  <span className="text-sm md:text-[0.95rem] text-white">
                    {t("sidebar.myClients")}
                  </span>
                </div>
              </li>
              <div className="divider" />
            </>
          )}

          {/* Monitoring KPI tab */}
          <li
            className="segment"
            data-id="monitoring"
            data-active={isMonitoringActive ? "true" : "false"}
            style={{
              ["--amb" as any]: "245 158 11",
              ["--overscan" as any]: "10px",
            }}
          >
            <button
              type="button"
              onClick={() => {
                if (currentModule !== "monitoring") {
                  trackModuleSwitch(currentModule, "monitoring");
                  setCurrentModule("monitoring");
                }
                if (!location.startsWith("/chat")) {
                  setLocation("/chat/monitoring");
                }
              }}
              className="focus-visible:outline-none"
              aria-current={isMonitoringActive ? "page" : undefined}
            />
            <div className="content">
              <span className="h-5 w-5 text-white relative">
                <Activity className="w-5 h-5" />
                {monitoringAlertCount > 0 && (
                  <span className="absolute -top-1.5 -right-1.5 w-4 h-4 bg-red-500 rounded-full flex items-center justify-center text-[9px] font-bold text-white">
                    {monitoringAlertCount > 9 ? "9+" : monitoringAlertCount}
                  </span>
                )}
              </span>
              <span className="text-sm md:text-[0.95rem] text-white">Monitoring</span>
            </div>
          </li>
          <div className="divider" />

          {/* Home tab — start menu */}
          <li
            className="segment"
            data-id="home"
            data-active={isHomeActive ? "true" : "false"}
            style={{
              ["--amb" as any]: "239 62 41",
              ["--overscan" as any]: "10px",
            }}
          >
            <button
              type="button"
              onClick={() => {
                trackModuleSwitch(currentModule, "home");
                setCurrentModule("home");
                setLocation("/chat/home");
              }}
              className="focus-visible:outline-none"
              aria-current={isHomeActive ? "page" : undefined}
            />
            <div className="content">
              <span className="h-5 w-5 text-white">
                <Sparkles className="w-5 h-5" />
              </span>
              <span className="text-sm md:text-[0.95rem] text-white">Start</span>
            </div>
          </li>
          <div className="divider" />

          {visibleModules.map((module) => {
            const config = MODULE_CONFIG[module.id];
            const label = config ? t(config.labelKey, { defaultValue: module.name }) : module.name;
            const isActive =
              !isDashboardActive &&
              !isManagerDashboardActive &&
              !isMonitoringActive &&
              !isHomeActive &&
              currentModule === module.id;
            const ambientColor = COLORS[module.id as keyof typeof COLORS] || "147 52 233";

            return (
              <Fragment key={module.id}>
                <li
                  className="segment"
                  data-id={module.id}
                  data-active={isActive ? "true" : "false"}
                  style={{
                    ["--amb" as any]: ambientColor,
                    ["--overscan" as any]: "10px",
                  }}
                >
                  {/* Hit-area = cały segment, pełna wysokość */}
                  <button
                    type="button"
                    onClick={() => handleModuleChange(module.id, module.locked || false)}
                    disabled={module.locked || false}
                    className="focus-visible:outline-none disabled:cursor-not-allowed"
                    aria-current={isActive ? "page" : undefined}
                  />

                  {/* Treść segmentu - container queries zarządzają widocznością */}
                  <div className={`content ${module.locked ? "opacity-60" : ""}`}>
                    <span className="h-5 w-5 text-white">
                      {getModuleIcon(module.id, "w-5 h-5", "default")}
                    </span>
                    {/* Container queries ukryją tekst gdy za mało miejsca */}
                    <span className="text-sm md:text-[0.95rem] text-white">{label}</span>
                    {module.locked && <Lock className="w-3 h-3 text-white/60 mt-0.5" />}
                  </div>
                </li>

                {/* Divider po każdym przycisku */}
                <div className="divider" />
              </Fragment>
            );
          })}
        </ul>
      </div>
    </nav>
  );
}
